// refusalRegister.js — the Refusal Register vocabulary + shape.
//
// PURE, NO I/O. Shared by the API (postCase validation + the submit hook) and the
// SPA (via the @shared alias), so the category string, the type_specific keys and
// the tag identity can never drift between the two sides.
//
// A Refusal Register entry is a Near Miss filed under Compliance recording a booking
// or procedure that was REFUSED (e.g. no acceptable ID under Challenge 25). It closes
// on submission and carries a locked tag so clinics can pull their own register for
// an audit. Spec: refusal_register_logic.md.

/** The incident_category that marks a near miss as a register entry. */
export const REFUSAL_REGISTER_CATEGORY = 'Refusal Register';

/** The only reporting function the category is offered under (and accepted for). */
export const REFUSAL_REGISTER_FUNCTION = 'Compliance';

/**
 * The locked system tag. `is_system = 1` and NO rule:
 *   • postCaseTag 403s a manual add, deleteCaseTag 403s a removal, and
 *   • autoTagReconcile only ever touches rule-BEARING system tags,
 * so once applied it cannot come off. Seeded by migration 031; ensured at runtime
 * too, so an API deployed ahead of its migration still tags correctly.
 */
export const REFUSAL_REGISTER_TAG = Object.freeze({
  slug:   'refusal_register',
  label:  'RefusalRegister',
  colour: 'tag-blue',        // allowlisted class hint (tagDto.TAG_COLOUR_CLASSES)
});

/** type_specific keys owned by the register (camelCase, as the other event types). */
export const REFUSAL_TS_KEYS = Object.freeze({
  requestedAt:      'refusalRequestedAt',       // 'YYYY-MM-DDTHH:mm' — the exact moment
  buyerDescription: 'refusalBuyerDescription',  // brief identifier, never an ID document
  actionTaken:      'refusalActionTaken',       // appointment denied, nothing arranged
});

/** Labels + input types — drives the wizard section, the case file and its editor. */
export const REFUSAL_FIELD_META = Object.freeze({
  [REFUSAL_TS_KEYS.requestedAt]:      { label: 'Date and time of the request', type: 'datetime-local' },
  [REFUSAL_TS_KEYS.buyerDescription]: { label: 'Description of the potential buyer', type: 'textarea' },
  [REFUSAL_TS_KEYS.actionTaken]:      { label: 'Action taken', type: 'textarea' },
});

/** Resolution text stamped on the case when it auto-closes. */
export const REFUSAL_AUTO_CLOSE_RESOLUTION =
  'Refusal recorded in the Refusal Register — the appointment was denied and no arrangements were made. '
  + 'Closed automatically on submission; the entry is retained for audit.';

/** History label for the automatic close. */
export const REFUSAL_AUTO_CLOSE_LABEL = 'Case closed automatically — refusal register entry recorded';

/** The rule name written into the close event's metadata. */
export const REFUSAL_AUTO_CLOSE_RULE = 'refusal-register-auto-close';

const blank = (v) => v == null || String(v).trim() === '';

/**
 * Is this a Refusal Register entry? Near Miss + the register category — nothing else.
 * Tolerates either shape (wire body or `cases` row).
 */
export function isRefusalRegisterCase({ eventType, incidentCategory } = {}) {
  return String(eventType || '') === 'NearMiss'
      && String(incidentCategory || '').trim() === REFUSAL_REGISTER_CATEGORY;
}

/** 'YYYY-MM-DDTHH:mm' (seconds tolerated) — the register needs date AND time. */
export const REFUSAL_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

/** The date part of a refusal datetime, or '' — used to stamp date_of_incident. */
export function refusalDateOf(requestedAt) {
  const v = String(requestedAt || '');
  return REFUSAL_DATETIME_RE.test(v) ? v.slice(0, 10) : '';
}

/**
 * Validate a register submission. Returns an array of human messages (empty = ok).
 * Used as the server backstop AND mirrored by the wizard's blocking validation, so
 * an API caller cannot file a register entry the form would have refused.
 *
 * @param {object} p
 *   typeSpecific         — the parsed bag
 *   incidentDescription  — the "details of the incident" narrative
 *   staffMemberConcerned — who refused the request
 */
export function validateRefusalRegister({ typeSpecific, incidentDescription, staffMemberConcerned } = {}) {
  const ts = (typeSpecific && typeof typeSpecific === 'object') ? typeSpecific : {};
  const errors = [];
  if (String(ts.reportingFunction || '') !== REFUSAL_REGISTER_FUNCTION) {
    errors.push(`A ${REFUSAL_REGISTER_CATEGORY} entry must be reported under ${REFUSAL_REGISTER_FUNCTION}.`);
  }
  const requestedAt = ts[REFUSAL_TS_KEYS.requestedAt];
  if (blank(requestedAt)) errors.push('Date and time of the request is required.');
  else if (!REFUSAL_DATETIME_RE.test(String(requestedAt).trim())) {
    errors.push('Date and time of the request must be a date and time (YYYY-MM-DDTHH:mm).');
  }
  if (blank(incidentDescription))             errors.push('Details of the incident are required.');
  if (blank(ts[REFUSAL_TS_KEYS.buyerDescription])) errors.push('Description of the potential buyer is required.');
  if (blank(ts[REFUSAL_TS_KEYS.actionTaken]))      errors.push('Action taken is required.');
  if (blank(staffMemberConcerned))            errors.push('The staff member who refused the request is required.');
  return errors;
}
